﻿import AirDatepicker from "air-datepicker";
import { dbfetch } from "./dbfetch";
import { Globalize } from "./_globalize";
import { createElement, uniqueId } from "./_dom";
import * as Res from "./res";
import { Sortable } from "./dragAndDrop";

let globalizedCalendar;

/**
 * 
 * @param {Date} start
 * @param {number} firstDay
 * @param {number} days
 */
function dateRange(start, days) {
	const result = [start];
	if (days > 1) {
		const date = new Date(start.valueOf());
		date.setDate(date.getDate() + days - 1);
		result.push(date);
	}
	return result;
}

function formatIsoDate(date) {
	return `${date.getFullYear().toString().padStart(4, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
}

function roundDate(date, firstDay) {
	date = new Date(date.valueOf());
	if (firstDay < 0) {
		date.setDate(1);
	} else {
		const dow = date.getDay();
		const delta = dow >= firstDay ? dow - firstDay : dow - firstDay + 7;
		date.setDate(date.getDate() - delta);
	}
	return date;
}

function createMonthYearPicker(header) {
	if (!globalizedCalendar) {
		globalizedCalendar = Globalize.culture().calendar;
	}
	const table = header.closest("table");
	const weekly = table.tBodies.length === 1;
	const calendarPopup = createElement("div", { id: uniqueId("v3-calendar-popup"), class: "v3-popup v3-calendar__popup" });
	const dateText = weekly ? header.innerText.split(" - ")[0] : header.innerText;
	const date = Globalize.parseDate(dateText) || false;
	/** @type HTMLAnchorElement */
	const todayURL = new URL(table.dataset.todayUrl, location.href);

	/*const picker = */new AirDatepicker(calendarPopup, {
		toggleSelected: weekly,
		range: weekly,
		moveToOtherMonthsOnSelect: !weekly,
		classes: weekly ? "air-datepicker-weekly" : undefined,
		showOtherMonths: true,
		selectOtherMonths: true,
		inline: true,
		view: weekly ? 'days' : 'months',
		minView: weekly ? 'days' : 'months',
		dateFormat: 'yyyy-MM-dd',
		startDate: date,
		selectedDates: dateRange(date, weekly ? 7 : 1),
		buttons: [{
			content: weekly ? Res.getString("view.calendar.this_week") : Res.getString("view.calendar.this_month"),
			onClick: (dp) => {
				const date = new Date(todayURL.searchParams.get("start"));
				dp.selectDate(date);
				dp.setViewDate(date);
			}
		}],
		locale: {
			days: globalizedCalendar.days.names,
			daysShort: globalizedCalendar.days.namesAbbr,
			daysMin: globalizedCalendar.days.namesShort,
			months: globalizedCalendar.months.names.slice(0, 12),
			monthsShort: globalizedCalendar.months.namesAbbr.slice(0, 12),
			firstDay: globalizedCalendar.firstDay
		},
		onFocus({ date, datepicker }) {
			if (weekly) {
				date = roundDate(date, globalizedCalendar.firstDay);
				datepicker.clear({ silent: true });
				datepicker.selectDate(dateRange(date, 7), { silent: true });
			}
		},
		onSelect({ date/*, formattedDate*/, datepicker }) {
			date = roundDate(date, weekly ? globalizedCalendar.firstDay : -1);
			let formattedDate;
			if (weekly) {
				const dates = dateRange(date, 7);
				formattedDate = `${Globalize.format(dates[0], "d")} - ${Globalize.format(dates[1], "d")}`;
				datepicker.clear({ silent: true });
				datepicker.selectDate(dates, { silent: true });
			} else {
				formattedDate = Globalize.format(date, "Y");
			}
			header.innerText = formattedDate;
			datepicker.hide();
			const page = todayURL.pathname;
			const params = new URLSearchParams(todayURL.search);
			params.set("start", formatIsoDate(date)); 
			// if navigating from view.aspx, use replace to avoid back button issues
			// if navigating from other pages, use assing to allow back button to previous location
			const useReplace = location.pathname.endsWith("/view.aspx");
			setTimeout(() => location[useReplace ? "replace" : "assign"](`${page}?${params}`), 0);
		}
	});
	return calendarPopup;
}

function initDragAndDrop(view) {
	const viewId = view.dataset.viewid;
	const fid = view.dataset.fid;
	const groupBase = `view-${viewId}-`;

	const updateRecord = async (dragElement, dragTarget) => {
		const reload = (anchor) => {
			var url = location.href;
			var hash = url.indexOf("#");
			if (hash >= 0)
				url = url.substring(0, hash);
			if (anchor)
				url += "#" + anchor;
			location.href = url;
			location.reload();
		};
		const id = dragElement.dataset["rowid"];
		const to = dragTarget.dataset["basevalue"];
		var data = new FormData();
		data.append(`f_${fid}`, to);
		try {
			const response = await dbfetch(
				`js.aspx?cmd=rowsave&v=${encodeURIComponent(viewId)}&id=${encodeURIComponent(id)}`, {
				method: "POST",
				body: data
			});
			if (response.ok) {
				const data = await response.json();
				if (data.controldata && data.controldata.invalid) {
					var i = data.controldata.invalid, err = [];
					if (i.common) Array.prototype.push.apply(err, i.common);
					for (var k in i) {
						if (k != "common")
							err.push(i[k]);
					}
					alert("There are errors:\r\n\r\n* " + err.join("\r\n* "));
				}
			} else {
				throw new Error(response.statusText);
			}
		} catch (e) {
			const msg = e.message;
			alert(msg);
			console.error("Item #" + id, " status change error:", msg);
		}
		reload(dragElement.id);
	};

	view.querySelectorAll(".js-draglist[data-basevalue]").forEach(list => {
		const baseDate = list.dataset.basevalue;
		Sortable.create(list, {
			revertOnSpill: true,
			group: {
				name: groupBase + baseDate,
				pull: true,
				// do not allow dropping to the same group
				put: (to, from) => {
					return from.options.group.name.startsWith(groupBase) && to.options.group.name !== from.options.group.name;
				}
			},
			sort: false,
			draggable: ".js-draggable",
			dataIdAttr: "data-rowid",
			dragClass: "v3-calendar__item--dragging",
			ghostClass: "v3-calendar__item--dragging-ghost",
			onAdd: event => {
				const { item, from, to } = event;
				if(from != to)
					updateRecord(item, to);
			}
		});
	});
}


function init(context, selector = ".v3-calendar[data-viewid]") {
	(context || document).querySelectorAll(selector).forEach(c => {
		const viewId = c.dataset.viewid;
		const /** @type {HTMLElement}*/header = c.querySelector(".v3-calendar__head-title");
		const calendarPopup = createMonthYearPicker(header, viewId);
		header.parentNode.append(calendarPopup);
		header.classList.add("js-auto-popup", "v3-popup-trigger");
		header.id = uniqueId("v3-calendar-trigger");
		header.setAttribute("aria-haspopup", "true");
		header.setAttribute("aria-controls", calendarPopup.id);
		initDragAndDrop(c);
	});
}

export { init };